import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { CurrentUser, TermSheet, TermSheetRevision } from '../../../shared/models';

interface TermSheetActionButtonProps {
	dataRoomGuid: string | undefined;
	currentUser: CurrentUser;
	sheet: TermSheet;
	currentRevision: TermSheetRevision | null;
	enterFeedback: (revision: TermSheetRevision | null) => void;
	editRevision: (revision: TermSheetRevision | null) => void;
	newRevision: (revision: TermSheetRevision | null) => void;
	deleteTermSheet: (sheet: TermSheet | null) => void;
	revisionList: (sheet: TermSheet | null) => void;
}

type TermSheetActionItem = keyof typeof TermSheetActionItemMap;
const TermSheetActionItemMap = {
	Enter: 'Enter Feedback',
	Edit: 'Edit',
	Revisions: 'Revisions',
	NewRevision: 'New Revision',
	Delete: 'Delete',
};

export const TermSheetActionButton: React.FC<TermSheetActionButtonProps> = ({
	dataRoomGuid,
	currentUser,
	sheet,
	currentRevision,
	enterFeedback,
	editRevision,
	newRevision,
	deleteTermSheet,
	revisionList,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: TermSheetActionItem) => {
		switch (action) {
			case 'Enter':
				enterFeedback(currentRevision ?? null);
				break;
			case 'Edit':
				editRevision(currentRevision ?? null);
				break;
			case 'Delete':
				deleteTermSheet(sheet);
				break;
			case 'NewRevision':
				newRevision(currentRevision ?? null);
				break;
			case 'Revisions':
				revisionList(sheet);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		actionItem: TermSheetActionItem
	) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList autoFocusItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'Enter')}>
										{TermSheetActionItemMap['Enter']}
									</MenuItem>
									{currentUser.isEmployeeUser && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Edit')}>
											{TermSheetActionItemMap['Edit']}
										</MenuItem>
									)}
									<MenuItem onClick={event => handleMenuItemClick(event, 'NewRevision')}>
										{TermSheetActionItemMap['NewRevision']}
									</MenuItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'Revisions')}>
										{TermSheetActionItemMap['Revisions']}
									</MenuItem>
									{currentUser.isEmployeeUser && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Delete')}>
											{TermSheetActionItemMap['Delete']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};
